import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import SEO from "@/components/site/SEO";
import { Clock, ArrowUpRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/blog/posts`).then((res) => {
      setPosts(res.data || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const [featured, ...rest] = posts;

  return (
    <div data-testid="page-blog">
      <SEO title="Blog" description="Stories, engineering posts and product updates from the team at SECQRE." path="/blog" />

      <section className="pt-32 md:pt-40 pb-12">
        <div className="container-secqre">
          <span className="overline">— Journal</span>
          <h1 className="mt-4 font-heading font-black text-5xl sm:text-6xl lg:text-7xl tracking-tight" data-testid="blog-title">
            Stories from the<br />privacy front line.
          </h1>
        </div>
      </section>

      {loading && (
        <section className="pb-24">
          <div className="container-secqre">
            <div className="grid md:grid-cols-3 gap-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="rounded-2xl border border-[#E2E8F0] bg-white p-6 animate-pulse">
                  <div className="aspect-[16/10] bg-[#F8F9FA] rounded-lg" />
                  <div className="h-4 bg-[#F8F9FA] rounded mt-5 w-3/4" />
                  <div className="h-3 bg-[#F8F9FA] rounded mt-3 w-1/2" />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {!loading && featured && (
        <section className="pb-12">
          <div className="container-secqre">
            <Link to={`/blog/${featured.slug}`} className="group block rounded-2xl border border-[#E2E8F0] bg-white overflow-hidden transition-all hover:-translate-y-1 hover:border-[#0F172A]/40" data-testid="blog-featured">
              <div className="grid lg:grid-cols-2 gap-0">
                <div className="aspect-[16/10] lg:aspect-auto overflow-hidden bg-[#F8F9FA]">
                  <img src={featured.cover} alt={featured.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" loading="lazy" />
                </div>
                <div className="p-8 md:p-12 flex flex-col justify-center">
                  <div className="flex items-center gap-3 text-xs">
                    <span className="px-2.5 py-1 rounded-full bg-[#FFD600] text-[#0A0A0A] font-bold">{featured.category}</span>
                    <span className="inline-flex items-center gap-1.5 text-[#475569]"><Clock className="w-3.5 h-3.5" /> {featured.read_time}</span>
                  </div>
                  <h2 className="mt-5 font-heading font-bold text-3xl md:text-4xl leading-tight">{featured.title}</h2>
                  <p className="mt-4 text-[#475569] leading-relaxed">{featured.excerpt}</p>
                  <div className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-[#0A0A0A]">
                    Read story <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </div>
                </div>
              </div>
            </Link>
          </div>
        </section>
      )}

      {!loading && rest.length > 0 && (
        <section className="pb-24">
          <div className="container-secqre">
            <div className="grid md:grid-cols-3 gap-5 md:gap-6">
              {rest.map((p, i) => (
                <Link key={p.slug} to={`/blog/${p.slug}`} className="group block rounded-2xl border border-[#E2E8F0] bg-white overflow-hidden transition-all hover:-translate-y-1 hover:border-[#0F172A]/40" data-testid={`blog-card-${i}`}>
                  <div className="aspect-[16/10] overflow-hidden bg-[#F8F9FA]">
                    <img src={p.cover} alt={p.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" loading="lazy" />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-3 text-xs">
                      <span className="px-2 py-0.5 rounded-full border border-[#E2E8F0] text-[#475569] font-medium">{p.category}</span>
                      <span className="inline-flex items-center gap-1.5 text-[#475569]"><Clock className="w-3.5 h-3.5" /> {p.read_time}</span>
                    </div>
                    <h3 className="mt-4 font-heading font-bold text-xl leading-snug">{p.title}</h3>
                    <p className="mt-2 text-sm text-[#475569] line-clamp-2">{p.excerpt}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
